export type AttentionIndexValues = {
  attention: number;
};

export type AttentionSampleSource = 'sdk-base' | 'server';

export type AttentionDecisionSample<TValues extends AttentionIndexValues> = {
  receivedAt: number;
  source?: AttentionSampleSource;
  values: TValues;
};

export type AttentionDecisionMode = 'absent' | 'low';

export type AttentionDecisionInput<TValues extends AttentionIndexValues> = {
  now: number;
  serverSample: AttentionDecisionSample<TValues> | null;
  rawSample: AttentionDecisionSample<TValues> | null;
  averagedAttention: number;
  firstIndexAdded: boolean;
  isPlaybackBuffering: boolean;
  lastHighAttentionAt: number;
  serverFreshMs: number;
  rawFreshMs: number;
  increaseAttentionValue: number;
  reductionAttentionValue: number;
  absentAttentionValue: number;
  lowAttentionGraceMs: number;
  absentAttentionGraceMs: number;
  lowAttentionSustainMs: number;
  absentAttentionSustainMs: number;
  detectorIdleAbsent?: boolean;
  suppressRawAbsentWhenServerFresh?: boolean;
};

export type AttentionDecisionDiagnostics = {
  attention: number;
  attentionSource: AttentionSampleSource | 'averaged';
  averagedAttention: number;
  firstIndexAdded: boolean;
  hasFreshAttentionSignal: boolean;
  hasDecisionAbsentSignal: boolean;
  hasDecisionRawAbsentSignal: boolean;
  hasDetectorIdleAbsentSignal: boolean;
  hasRawAbsentSignal: boolean;
  hasServerAbsentSignal: boolean;
  isRawAbsentSuppressedByServer: boolean;
  rawAttention: number | null;
  rawAttentionSource: AttentionSampleSource | 'stale';
  rawSignalAge: number | null;
  serverSignalAge: number | null;
  increaseAttentionValue: number;
  reductionAttentionValue: number;
  timeSinceHighAttention: number;
  isPlaybackBuffering: boolean;
};

export type AttentionDecisionResult = {
  decisionAttention: number;
  decisionAttentionSource: AttentionSampleSource | 'averaged';
  decisionHardAbsent: boolean;
  isLow: boolean;
  isHigh: boolean;
  isBelowThreshold: boolean;
  lowAttentionMode: AttentionDecisionMode;
  lowAttentionGraceMs: number;
  lowAttentionSustainMs: number;
  diagnostics: AttentionDecisionDiagnostics;
};

const getSampleAge = <TValues extends AttentionIndexValues>(
  now: number,
  sample: AttentionDecisionSample<TValues> | null
) => (sample ? now - sample.receivedAt : Number.POSITIVE_INFINITY);

const finiteAgeOrNull = (age: number) => (Number.isFinite(age) ? age : null);

export const resolveAttentionDecision = <TValues extends AttentionIndexValues>({
  now,
  serverSample,
  rawSample,
  averagedAttention,
  firstIndexAdded,
  isPlaybackBuffering,
  lastHighAttentionAt,
  serverFreshMs,
  rawFreshMs,
  increaseAttentionValue,
  reductionAttentionValue,
  absentAttentionValue,
  lowAttentionGraceMs,
  absentAttentionGraceMs,
  lowAttentionSustainMs,
  absentAttentionSustainMs,
  detectorIdleAbsent = false,
  suppressRawAbsentWhenServerFresh = false,
}: AttentionDecisionInput<TValues>): AttentionDecisionResult => {
  const serverSignalAge = getSampleAge(now, serverSample);
  const rawSignalAge = getSampleAge(now, rawSample);
  const hasRecentServerAttention =
    Boolean(serverSample) && serverSignalAge <= serverFreshMs;
  const hasRecentRawAttention =
    Boolean(rawSample) && rawSignalAge <= rawFreshMs;
  const rawAttention = hasRecentRawAttention
    ? rawSample!.values.attention
    : null;
  const rawAttentionSource = hasRecentRawAttention
    ? (rawSample!.source ?? 'server')
    : 'stale';
  const recentRawAttentionSource: AttentionSampleSource = hasRecentRawAttention
    ? (rawSample!.source ?? 'server')
    : 'server';
  const decisionAttention = hasRecentServerAttention
    ? serverSample!.values.attention
    : rawAttention !== null
      ? rawAttention
      : averagedAttention;
  const decisionAttentionSource = hasRecentServerAttention
    ? 'server'
    : rawAttention !== null
      ? recentRawAttentionSource
      : 'averaged';
  const hasFreshAttentionSignal =
    hasRecentServerAttention || hasRecentRawAttention;
  const hasRawAbsentSignal =
    rawAttention !== null && rawAttention <= absentAttentionValue;
  const hasServerAbsentSignal =
    hasRecentServerAttention &&
    serverSample!.values.attention <= absentAttentionValue;
  const isRawAbsentSuppressedByServer =
    suppressRawAbsentWhenServerFresh &&
    hasRecentServerAttention &&
    serverSample!.values.attention > absentAttentionValue;
  const hasDecisionRawAbsentSignal =
    hasRawAbsentSignal && !isRawAbsentSuppressedByServer;
  const hasDetectorIdleAbsentSignal =
    detectorIdleAbsent && !isRawAbsentSuppressedByServer;
  const hasDecisionAbsentSignal =
    hasDecisionRawAbsentSignal ||
    hasServerAbsentSignal ||
    hasDetectorIdleAbsentSignal;
  const isBelowThreshold =
    firstIndexAdded &&
    hasFreshAttentionSignal &&
    decisionAttention <= reductionAttentionValue;
  const isHigh =
    hasFreshAttentionSignal && decisionAttention >= increaseAttentionValue;
  const lowAttentionMode: AttentionDecisionMode = hasDecisionAbsentSignal
    ? 'absent'
    : 'low';
  const selectedLowAttentionGraceMs = hasDecisionAbsentSignal
    ? absentAttentionGraceMs
    : lowAttentionGraceMs;
  const selectedLowAttentionSustainMs = hasDecisionAbsentSignal
    ? absentAttentionSustainMs
    : lowAttentionSustainMs;
  const timeSinceHighAttention = now - lastHighAttentionAt;
  const decisionHardAbsent =
    firstIndexAdded && !isPlaybackBuffering && hasDecisionAbsentSignal;
  const isLow =
    firstIndexAdded &&
    !isPlaybackBuffering &&
    isBelowThreshold &&
    timeSinceHighAttention >= selectedLowAttentionGraceMs;

  return {
    decisionAttention,
    decisionAttentionSource,
    decisionHardAbsent,
    isLow,
    isHigh,
    isBelowThreshold,
    lowAttentionMode,
    lowAttentionGraceMs: selectedLowAttentionGraceMs,
    lowAttentionSustainMs: selectedLowAttentionSustainMs,
    diagnostics: {
      attention: decisionAttention,
      attentionSource: decisionAttentionSource,
      averagedAttention,
      firstIndexAdded,
      hasFreshAttentionSignal,
      hasDecisionAbsentSignal,
      hasDecisionRawAbsentSignal,
      hasDetectorIdleAbsentSignal,
      hasRawAbsentSignal,
      hasServerAbsentSignal,
      isRawAbsentSuppressedByServer,
      rawAttention,
      rawAttentionSource,
      rawSignalAge: finiteAgeOrNull(rawSignalAge),
      serverSignalAge: finiteAgeOrNull(serverSignalAge),
      increaseAttentionValue,
      reductionAttentionValue,
      timeSinceHighAttention,
      isPlaybackBuffering,
    },
  };
};
